import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RefreshCw, Monitor, RotateCcw, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';

const TYPE_LABEL = {
  algo_off:        'Algo Trading OFF',
  logged_out:      'MT5 Logged Out',
  bridge_dead:     'Bridge Dead',
  container_down:  'Container Down',
  auth_failed:     'Auth Failed',
  unknown:         'Unknown',
};

function SeverityBadge({ severity }) {
  const color = severity === 'critical' ? 'var(--loss)' : '#e0a800';
  return (
    <span style={{
      display:'inline-block', padding:'2px 8px', borderRadius:4,
      background:color, color:'#fff', fontSize:11, fontWeight:600,
      textTransform:'uppercase', letterSpacing:'0.05em',
    }}>{severity}</span>
  );
}

function relTime(iso) {
  if (!iso) return '—';
  const ageSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (ageSec < 60)   return `${Math.round(ageSec)}s ago`;
  if (ageSec < 3600) return `${Math.round(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.round(ageSec / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

export default function Errors() {
  const [incidents, setIncidents] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/incidents', { params: { status: filter, limit: 200 } });
      setIncidents(r.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 15_000);
    const sock = getSocket();
    const refresh = () => load();
    if (sock) {
      sock.on('incident:opened',   refresh);
      sock.on('incident:resolved', refresh);
    }
    return () => {
      clearInterval(interval);
      if (sock) {
        sock.off('incident:opened',   refresh);
        sock.off('incident:resolved', refresh);
      }
    };
  }, [load]);

  async function resolve(id) {
    setBusy(b => ({ ...b, [id]: 'resolve' }));
    try {
      await api.put(`/admin/incidents/${id}/resolve`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(b => ({ ...b, [id]: null }));
    }
  }

  async function restart(userId, incidentId) {
    if (!window.confirm('Restart the user\'s MT5 container? This will fully recreate it.')) return;
    setBusy(b => ({ ...b, [incidentId]: 'restart' }));
    try {
      await api.post(`/admin/users/${userId}/restart-container`);
      // Container is async; rely on watcher auto-resolve
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(b => ({ ...b, [incidentId]: null }));
    }
  }

  async function openVnc(userId) {
    try {
      const r = await api.get(`/admin/vnc-access/${userId}`);
      const { hostIp, hostPort, vncPassword } = r.data;
      // H6: noVNC binds 127.0.0.1 on the server — SSH tunnel only, so show
      // the recipe instead of opening a tab that can never connect.
      alert(
        `noVNC is loopback-only on the server (H6 hardening).\n\n` +
        `1. Tunnel:   ssh -L ${hostPort}:${hostIp || '127.0.0.1'}:${hostPort} <server>\n` +
        `2. Open:     http://localhost:${hostPort}/vnc.html\n` +
        `3. Password: ${vncPassword}`
      );
    } catch (err) {
      alert(`Cannot open GUI: ${err.response?.data?.error || err.message}`);
    }
  }

  const openCount = incidents.filter(i => i.status === 'open').length;

  return (
    <>
      <div className="ct-page-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <span className="eyebrow">Admin</span>
          <h1 style={{display:'inline-flex', alignItems:'center', gap:10}}>
            <AlertTriangle size={22} color="var(--loss)" /> Errors
            {openCount > 0 && (
              <span style={{
                marginLeft:8, padding:'2px 10px', borderRadius:12,
                background:'var(--loss)', color:'#fff', fontSize:13,
              }}>{openCount} open</span>
            )}
          </h1>
          <div className="subtitle">Container health incidents — refreshes every 15s + live</div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <select className="form-control form-control-sm" style={{width:160}}
            value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="open">Open only</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          <button className="btn btn-sm btn-secondary" onClick={load}
            style={{display:'inline-flex',alignItems:'center',gap:6}}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{padding:0}}>
          {loading ? <p className="text-muted text-center py-4">Loading...</p> :
           incidents.length === 0 ? (
            <p className="text-center py-5" style={{color:'var(--profit)'}}>
              <CheckCircle2 size={20} style={{verticalAlign:'middle', marginRight:6}} />
              No {filter === 'all' ? '' : filter} incidents. All containers healthy.
            </p>
          ) : (
            <div className="table-responsive table-cards table-cards-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Type</th>
                    <th>User</th>
                    <th>Message</th>
                    <th>Opened</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.map(inc => {
                    const userId = inc.userId?._id || inc.userId;
                    const email  = inc.userId?.email || '—';
                    return (
                      <tr key={inc._id} style={inc.status === 'open' ? {} : {opacity:0.55}}>
                        <td data-label="Severity"><SeverityBadge severity={inc.severity} /></td>
                        <td data-label="Type"><strong>{TYPE_LABEL[inc.type] || inc.type}</strong></td>
                        <td data-label="User" className="small">{email}</td>
                        <td data-label="Message" className="small" style={{maxWidth:380, wordBreak:'break-word'}}>{inc.message}</td>
                        <td data-label="Opened" className="text-muted small">{relTime(inc.openedAt)}</td>
                        <td data-label="Status">
                          {inc.status === 'open' ? (
                            <span style={{color:'var(--loss)', fontWeight:600}}>OPEN</span>
                          ) : (
                            <span className="text-muted small">
                              resolved {relTime(inc.resolvedAt)}<br/>
                              <em>by {inc.resolvedBy || '—'}</em>
                            </span>
                          )}
                        </td>
                        <td data-label="Actions">
                          {inc.status === 'open' && (
                            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                              <button className="btn btn-sm btn-info"
                                title="Open noVNC"
                                disabled={busy[inc._id]}
                                onClick={() => openVnc(userId)}
                                style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <Monitor size={12} /> noVNC
                              </button>
                              <button className="btn btn-sm btn-warning"
                                title="Restart container"
                                disabled={busy[inc._id]}
                                onClick={() => restart(userId, inc._id)}
                                style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <RotateCcw size={12} /> {busy[inc._id] === 'restart' ? '...' : 'Restart'}
                              </button>
                              <button className="btn btn-sm btn-success"
                                title="Mark resolved"
                                disabled={busy[inc._id]}
                                onClick={() => resolve(inc._id)}
                                style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                <CheckCircle2 size={12} /> {busy[inc._id] === 'resolve' ? '...' : 'Resolve'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
